import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { PublicUser } from '@avslack/shared';
import { browseChannels } from '../lib/api';
import { channelTitle, sortChannels, useChannels, useOpenDm } from '../data/channels';
import { useUsers } from '../data/users';
import { Dialog } from './Dialog';
import { PresenceDot } from './PresenceDot';
import { Avatar } from './Avatar';
import { Icon } from './Icon';

interface Entry {
  key: string;
  label: string;
  hint: string;
  group: string;
  channelIcon?: string;
  userId?: string;
  presence?: string;
  unread?: number;
  run: () => void | Promise<void>;
}

/** Ekezet-fuggetlen egyezes: "surgos" a "sürgős"-t is megtalalja, es forditva. */
const fold = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

export function QuickSwitcher({ me, onClose }: { me: PublicUser; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: myChannels } = useChannels();
  const { users, usersById, nameDisplay, presence, nameOf } = useUsers();
  const openDm = useOpenDm();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const optionId = (index: number) => `${listId}-opt-${index}`;

  const { data: publicChannels } = useQuery({
    queryKey: ['channels', 'browse'],
    queryFn: browseChannels,
    staleTime: 60_000,
  });

  const entries = useMemo<Entry[]>(() => {
    const goTo = (channelId: string) => () =>
      navigate({ to: '/c/$channelId', params: { channelId } });

    const mine = sortChannels(myChannels ?? []).map<Entry>((channel) => {
      const isDm = channel.kind === 'dm' || channel.kind === 'group_dm';
      const other = isDm ? (channel.memberIds ?? []).find((id) => id !== me.id) : undefined;

      return {
        key: `ch-${channel.id}`,
        label: channelTitle(channel, usersById, me.id, nameDisplay),
        hint: channel.isArchived
          ? 'archivált'
          : channel.membership.isHidden
            ? 'elrejtve a listából'
            : isDm
              ? 'közvetlen üzenet'
              : channel.kind === 'private'
                ? 'privát'
                : 'csatorna',
        group: isDm ? 'Beszélgetések' : 'Csatornáid',
        ...(isDm ? {} : { channelIcon: channel.kind === 'private' ? 'lakat' : 'csatorna' }),
        ...(other ? { userId: other, presence: presence[other] } : {}),
        unread: channel.unreadCount,
        run: goTo(channel.id),
      };
    });

    const joinedIds = new Set((myChannels ?? []).map((c) => c.id));
    const discoverable = (publicChannels ?? [])
      .filter((channel) => !joinedIds.has(channel.id))
      .map<Entry>((channel) => ({
        key: `pub-${channel.id}`,
        label: channel.name ?? '',
        hint: `${channel.memberCount} tag`,
        group: 'Még nem vagy tag',
        channelIcon: '#',
        run: goTo(channel.id),
      }));

    const dmTargets = users
      .filter((user) => user.id !== me.id)
      .map<Entry>((user) => ({
        key: `user-${user.id}`,
        label: nameOf(user),
        hint: user.title ?? 'üzenet indítása',
        group: 'Emberek',
        userId: user.id,
        presence: presence[user.id],
        run: async () => {
          const result = await openDm.mutateAsync([user.id]);
          await navigate({ to: '/c/$channelId', params: { channelId: result.channel.id } });
        },
      }));

    return [...mine, ...discoverable, ...dmTargets];
  }, [
    myChannels,
    publicChannels,
    users,
    usersById,
    presence,
    me.id,
    navigate,
    openDm,
    nameOf,
    nameDisplay,
  ]);

  const filtered = useMemo(() => {
    const needle = fold(query.trim());
    if (!needle) return entries.slice(0, 14);
    return entries.filter((entry) => fold(entry.label).includes(needle)).slice(0, 14);
  }, [entries, query]);

  useEffect(() => setCursor(0), [query]);

  // A kijelolt sor mindig maradjon lathato billentyus navigalasnal.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({
      block: 'nearest',
    });
  }, [cursor]);

  const choose = (entry: Entry | undefined) => {
    if (!entry) return;
    void Promise.resolve(entry.run()).then(onClose);
  };

  let lastGroup = '';

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Gyorsváltó"
      description="Ugrás csatornára vagy emberre"
      width="lg"
      padded={false}
    >
      <div className="flex shrink-0 items-center gap-3 border-b border-border-soft px-4">
        <Icon name="keres" className="text-ink-faint" />
        <input
          aria-label="Csatorna vagy személy keresése"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={filtered.length > 0}
          aria-controls={listId}
          aria-activedescendant={filtered[cursor] ? optionId(cursor) : undefined}
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setCursor((c) => Math.min(c + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setCursor((c) => Math.max(c - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              choose(filtered[cursor]);
            }
          }}
          placeholder="Csatorna vagy név…"
          className="w-full bg-transparent py-4 text-base outline-none placeholder:text-ink-faint"
        />
      </div>

      {/*
        COMBOBOX-minta: a fokusz VEGIG a keresomezoben marad, a kijelolest az
        aria-activedescendant mondja meg a felolvasonak. Ezert a sorok nem
        gombok (az option-ben nem lehet interaktiv elem), hanem kattinthato
        option-ok - billentyuzettel a mezobol vezereljuk oket.
      */}
      <ul
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label="Találatok"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5"
      >
        {filtered.map((entry, index) => {
          const showGroup = entry.group !== lastGroup;
          lastGroup = entry.group;
          const active = index === cursor;

          return (
            <Fragment key={entry.key}>
              {showGroup && (
                <li
                  role="presentation"
                  className="px-2.5 pb-1 pt-3 text-2xs font-semibold uppercase tracking-[0.08em] text-ink-faint"
                >
                  {entry.group}
                </li>
              )}
              <li
                id={optionId(index)}
                role="option"
                aria-selected={active}
                data-active={active}
                onMouseEnter={() => setCursor(index)}
                onClick={() => choose(entry)}
                className={[
                  'cursor-pointer',
                  'flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left',
                  'transition-colors duration-(--duration-quick)',
                  active ? 'bg-accent-soft text-accent' : 'text-ink',
                ].join(' ')}
              >
                {entry.userId ? (
                  <span className="relative">
                    <Avatar userId={entry.userId} name={entry.label} size="sm" />
                    <PresenceDot
                      presence={entry.presence}
                      className={`absolute -bottom-0.5 -right-0.5 ring-2 ${active ? 'ring-accent-soft' : 'ring-surface-raised'}`}
                    />
                  </span>
                ) : (
                  <span
                    className={`grid size-6 shrink-0 place-items-center rounded-control text-sm ${
                      active ? 'text-accent' : 'text-ink-faint'
                    }`}
                  >
                    <Icon name={entry.channelIcon === 'lakat' ? 'lakat' : 'csatorna'} size="sm" />
                  </span>
                )}

                <span className="min-w-0 flex-1 truncate text-sm font-medium">{entry.label}</span>

                {entry.unread ? (
                  <span className="min-w-5 rounded-pill bg-alert px-1.5 text-center text-2xs font-bold leading-5 text-white">
                    {entry.unread > 99 ? '99+' : entry.unread}
                  </span>
                ) : null}

                <span
                  className={`shrink-0 text-xs ${active ? 'text-accent/80' : 'text-ink-faint'}`}
                >
                  {entry.hint}
                </span>
              </li>
            </Fragment>
          );
        })}
      </ul>

      <div role="status">
        {filtered.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-ink-muted">
            Nincs találat erre: <strong className="text-ink">{query}</strong>
          </p>
        )}
      </div>

      <footer className="flex shrink-0 items-center gap-3 border-t border-border-soft bg-surface-sunken px-4 py-2 text-2xs text-ink-faint">
        <span>
          <Key>
            <Icon name="nyilFel" />
          </Key>{' '}
          <Key>
            <Icon name="nyilLe" />
          </Key>{' '}
          lépkedés
        </span>
        <span>
          <Key>Enter</Key> megnyitás
        </span>
        <span>
          <Key>Esc</Key> bezárás
        </span>
      </footer>
    </Dialog>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-border-soft bg-surface px-1 font-sans text-2xs">
      {children}
    </kbd>
  );
}
